import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle, Clock, MessageSquare } from 'lucide-react';
import { complaintsApi } from '../../api/endpoints/complaints';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { SearchInput } from '../../components/ui/SearchInput';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import { StatCard } from '../../components/ui/StatCard';
import { useToast } from '../../components/ui/Alert';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { useDebounce } from '../../hooks/useDebounce';
import { usePagination } from '../../hooks/usePagination';
import { PERMISSIONS } from '../../lib/permissions';
import { formatDate, formatRelativeTime } from '../../lib/formatters';
import type { Complaint, ComplaintStatus, ComplaintPriority } from '../../types/complaint';

const STATUS_OPTIONS: ComplaintStatus[] = ['open', 'in_progress', 'resolved', 'closed'];
const PRIORITY_OPTIONS: ComplaintPriority[] = ['high', 'medium', 'low'];

const STATUS_VARIANTS: Record<ComplaintStatus, 'default' | 'info' | 'success' | 'warning' | 'danger'> = {
  open: 'danger',
  in_progress: 'warning',
  resolved: 'success',
  closed: 'default',
};

const PRIORITY_VARIANTS: Record<ComplaintPriority, 'default' | 'danger' | 'warning' | 'info'> = {
  high: 'danger',
  medium: 'warning',
  low: 'info',
};

export function ComplaintsPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const { page, limit, goToPage } = usePagination({
    resetDeps: [debouncedSearch, statusFilter, priorityFilter],
  });
  const [resolveTarget, setResolveTarget] = useState<Complaint | null>(null);
  const [resolution, setResolution] = useState('');
  const [viewTarget, setViewTarget] = useState<Complaint | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['complaints', storeId, { page, limit, search: debouncedSearch, status: statusFilter, priority: priorityFilter }],
    queryFn: () => complaintsApi.list(storeId!, {
      page,
      limit,
      search: debouncedSearch || undefined,
      status: statusFilter || undefined,
      priority: priorityFilter || undefined,
    }),
    enabled: !!storeId,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { status?: string; resolution?: string } }) =>
      complaintsApi.update(storeId!, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complaints', storeId] });
      toast('success', t('complaints.resolveSuccess'));
      setResolveTarget(null);
      setResolution('');
    },
    onError: () => toast('error', t('common.error')),
  });

  const handleResolve = () => {
    if (!resolveTarget || !resolution.trim()) return;
    updateMutation.mutate({
      id: resolveTarget._id,
      data: { status: 'resolved', resolution },
    });
  };

  const columns: Column<Complaint>[] = [
    {
      key: 'subject',
      header: t('complaints.subject'),
      render: (c) => (
        <div className="cursor-pointer" onClick={() => setViewTarget(c)}>
          <p className="text-sm font-medium">{c.subject}</p>
          {c.customer?.name && <p className="text-xs text-text-muted">{c.customer.name}</p>}
        </div>
      ),
    },
    {
      key: 'priority',
      header: t('complaints.priority'),
      render: (c) => (
        <Badge variant={PRIORITY_VARIANTS[c.priority]}>
          {t(`complaints.${c.priority}`)}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (c) => (
        <Badge variant={STATUS_VARIANTS[c.status]}>
          {t(`complaints.${c.status}`)}
        </Badge>
      ),
    },
    {
      key: 'date',
      header: t('common.date'),
      sortable: true,
      render: (c) => <span className="text-xs text-text-secondary">{formatRelativeTime(c.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (c) => (
        <PermissionGate requires={PERMISSIONS.COMPLAINTS_EDIT}>
          <div className="flex items-center gap-1">
            {(c.status === 'open' || c.status === 'in_progress') && (
              <button
                onClick={() => { setResolveTarget(c); setResolution(''); }}
                className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-success"
                title={t('complaints.resolve')}
              >
                <CheckCircle className="h-4 w-4" />
              </button>
            )}
          </div>
        </PermissionGate>
      ),
    },
  ];

  const complaints = data?.complaints ?? [];
  const total = data?.pagination?.total ?? 0;
  const totalPages = data?.pagination?.pages ?? 1;
  const openCount = complaints.filter(c => c.status === 'open').length;
  const inProgressCount = complaints.filter(c => c.status === 'in_progress').length;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">{t('complaints.title')}</h1>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <StatCard
          title={t('complaints.open')}
          value={openCount}
          icon={<AlertTriangle className="h-5 w-5" />}
        />
        <StatCard
          title={t('complaints.in_progress')}
          value={inProgressCount}
          icon={<Clock className="h-5 w-5" />}
        />
        <StatCard
          title={t('complaints.resolved')}
          value={complaints.filter(c => c.status === 'resolved').length}
          icon={<CheckCircle className="h-5 w-5" />}
        />
        <StatCard
          title={t('complaints.title')}
          value={total}
          icon={<MessageSquare className="h-5 w-5" />}
        />
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('common.search') + '...'}
          className="max-w-sm"
        />
        <Select
          options={[
            { value: '', label: t('complaints.allStatuses') },
            ...STATUS_OPTIONS.map(s => ({ value: s, label: t(`complaints.${s}`) })),
          ]}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
        <Select
          options={[
            { value: '', label: t('complaints.allPriorities') },
            ...PRIORITY_OPTIONS.map(p => ({ value: p, label: t(`complaints.${p}`) })),
          ]}
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
        />
      </div>

      <div className="rounded-xl border border-border-default bg-bg-secondary shadow-sm overflow-hidden">
        <DataTable
          columns={columns}
          data={complaints}
          isLoading={isLoading}
          keyExtractor={(c) => c._id}
          emptyTitle={t('complaints.empty')}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={goToPage}
        />
      </div>

      {/* View Complaint Modal */}
      <Modal
        isOpen={!!viewTarget}
        onClose={() => setViewTarget(null)}
        title={viewTarget?.subject ?? ''}
        size="lg"
      >
        {viewTarget && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Badge variant={PRIORITY_VARIANTS[viewTarget.priority]}>
                {t(`complaints.${viewTarget.priority}`)}
              </Badge>
              <Badge variant={STATUS_VARIANTS[viewTarget.status]}>
                {t(`complaints.${viewTarget.status}`)}
              </Badge>
              <span className="text-xs text-text-muted ms-auto">{formatDate(viewTarget.createdAt)}</span>
            </div>
            {viewTarget.customer && (
              <div className="text-sm text-text-secondary">
                {viewTarget.customer.name} &middot; {viewTarget.customer.email}
              </div>
            )}
            {viewTarget.orderNumber && (
              <p className="text-sm text-text-muted">
                {t('orders.orderNumber')}: #{viewTarget.orderNumber}
              </p>
            )}
            <div className="rounded-lg bg-bg-tertiary p-4">
              <p className="text-sm whitespace-pre-wrap">{viewTarget.message}</p>
            </div>
            {viewTarget.resolution && (
              <div className="rounded-lg border border-success/20 bg-success/5 p-4">
                <p className="text-xs font-medium text-success mb-1">{t('complaints.resolution')}</p>
                <p className="text-sm whitespace-pre-wrap">{viewTarget.resolution}</p>
              </div>
            )}
            {(viewTarget.status === 'open' || viewTarget.status === 'in_progress') && (
              <Button
                size="sm"
                onClick={() => { setResolveTarget(viewTarget); setResolution(''); setViewTarget(null); }}
              >
                <CheckCircle className="h-4 w-4" />
                {t('complaints.resolve')}
              </Button>
            )}
          </div>
        )}
      </Modal>

      {/* Resolve Complaint Modal */}
      <Modal
        isOpen={!!resolveTarget}
        onClose={() => setResolveTarget(null)}
        title={`${t('complaints.resolve')} - ${resolveTarget?.subject}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setResolveTarget(null)}>{t('common.cancel')}</Button>
            <Button
              isLoading={updateMutation.isPending}
              onClick={handleResolve}
              disabled={!resolution.trim()}
            >
              {t('complaints.resolve')}
            </Button>
          </>
        }
      >
        <Textarea
          id="resolution"
          label={t('complaints.resolution')}
          value={resolution}
          onChange={(e) => setResolution(e.target.value)}
          placeholder={t('complaints.resolutionPlaceholder')}
          rows={4}
        />
      </Modal>
    </div>
  );
}
